/**
 * Premium price matching — pure utility functions for subscription comparisons.
 *
 * Used by the frontend to compare subscription-based pricing (e.g. AH Premium)
 * against bonus offers, both within and across supermarkets.
 *
 * All functions are pure — no React, no store dependency.
 */

/** @import { Offer, CatalogMatch, PremiumComparison, Category } from '../types.js' */

const AH_PREMIUM_DISCOUNT = 0.10

/** Escape special regex characters in a string for safe use in `new RegExp()`. */
function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Detect multi-buy deals where the unit price is unchanged.
 * These are quantity-based promotions ("2+1 gratis", "3 voor 5.00")
 * where comparing per-unit price against Premium is misleading.
 */
const MULTI_BUY_PATTERN = /\d+\s*[+]\s*\d+\s*gratis|\d+\s+voor\s+\d/i

/**
 * Detect multi-buy deals where the unit price is unchanged.
 *
 * @param {Offer} offer
 * @returns {boolean}
 */
export function isMultiBuyDeal(offer) {
  if (!offer.discount) return false
  // Multi-buy: offerPrice equals shelf price (no per-unit reduction)
  const hasNoUnitDiscount = !offer.originalPrice || offer.originalPrice <= offer.offerPrice
  return MULTI_BUY_PATTERN.test(offer.discount) && hasNoUnitDiscount
}

/**
 * Compute the AH Premium price for a regular shelf price.
 *
 * @param {number} regularPrice
 * @returns {number} Rounded to 2 decimal places
 */
export function computePremiumPrice(regularPrice) {
  return Math.round(regularPrice * (1 - AH_PREMIUM_DISCOUNT) * 100) / 100
}

/**
 * Check if the Premium price undercuts a given offer price.
 *
 * @param {number} premiumPrice
 * @param {number} offerPrice
 * @returns {boolean}
 */
export function isPremiumCheaper(premiumPrice, offerPrice) {
  return premiumPrice < offerPrice
}

/**
 * Format savings between Premium price and offer price.
 *
 * @param {number} premiumPrice
 * @param {number} offerPrice
 * @returns {string|null} e.g. "€0,27 goedkoper" or null if no savings
 */
export function formatPremiumSaving(premiumPrice, offerPrice) {
  const diff = offerPrice - premiumPrice
  if (diff <= 0) return null
  return `€${diff.toFixed(2).replace('.', ',')} goedkoper`
}

/**
 * Normalize a product name for matching — strips supermarket prefixes and bio labels.
 * Same logic as normalizeProductKey in offerStore.js.
 */
function normalizeForMatching(name) {
  return name
    .toLowerCase()
    .replace(/^(ah|jumbo|plus|lidl|aldi|dirk|dekamarkt|ekoplaza|bio)\s+/gi, '')
    .replace(/\b(biologisch|biologische|bio|eko|organic)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Find the best AH catalog match for a product name in a given category.
 *
 * Matching is done by normalized product name — the same approach used for
 * cross-supermarket comparison grouping.
 *
 * @param {Array<CatalogMatch & { category: Category }>} catalogProducts - AH catalog products from the store
 * @param {string} productName - The offer's product name to match
 * @param {Category} category
 * @returns {CatalogMatch|null}
 */
export function findCatalogMatch(catalogProducts, productName, category) {
  if (!catalogProducts || catalogProducts.length === 0) return null

  const normalizedQuery = normalizeForMatching(productName)
  if (!normalizedQuery) return null

  // Try exact normalized match first
  let match = catalogProducts.find(p =>
    p.category === category && normalizeForMatching(p.productName) === normalizedQuery
  )

  // Fallback: full-word containment with a minimum similarity threshold.
  // Without these guards, a catalog product like "pasta" would match
  // "pompoenpitten pasta" — a completely different product AH doesn't carry.
  if (!match) {
    match = catalogProducts.find(p => {
      if (p.category !== category) return false
      const normalizedCatalog = normalizeForMatching(p.productName)

      // Determine which is shorter/longer
      const shorter = normalizedCatalog.length <= normalizedQuery.length ? normalizedCatalog : normalizedQuery
      const longer = normalizedCatalog.length <= normalizedQuery.length ? normalizedQuery : normalizedCatalog

      // The shorter string must be a substantial portion of the longer one
      // to prevent "pasta" matching "pompoenpitten pasta"
      if (shorter.length / longer.length < 0.6) return false

      // Check containment with word boundaries — the shorter string
      // must appear as complete words within the longer string
      const wordBoundary = new RegExp(`\\b${escapeRegExp(shorter)}\\b`)
      return wordBoundary.test(longer)
    })
  }

  if (!match) return null

  return {
    productName: match.productName,
    regularPrice: match.regularPrice,
    premiumPrice: computePremiumPrice(match.regularPrice),
    unit: match.unit,
    sourceUrl: match.sourceUrl,
  }
}

/**
 * Find an AH catalog match by semantic cluster ID.
 * Returns the same shape as findCatalogMatch, or null if no match.
 *
 * This is the preferred matching strategy — cluster IDs are assigned
 * by the embedding pipeline and guarantee semantic equivalence.
 */
function findCatalogMatchByCluster(catalogProducts, offer) {
  if (!offer.clusterId) return null

  const match = catalogProducts.find(p => p.clusterId === offer.clusterId)
  if (!match) return null

  return {
    productName: match.productName,
    regularPrice: match.regularPrice,
    premiumPrice: computePremiumPrice(match.regularPrice),
    unit: match.unit,
    sourceUrl: match.sourceUrl,
  }
}

/**
 * Determine the Premium comparison result for an offer.
 *
 * Matching strategy:
 * 1. Cluster match (semantic) — when both offer and catalog have clusterId
 * 2. String match (fallback) — normalized name comparison for old data
 *
 * @param {Offer} offer
 * @param {Array<CatalogMatch & { category: Category, clusterId?: number }>} catalogProducts
 * @returns {PremiumComparison|null}
 */
export function getPremiumComparison(offer, catalogProducts) {
  if (!catalogProducts || catalogProducts.length === 0) return null

  // Multi-buy deals ("2+1 gratis", "3 voor 5.00") use the regular shelf price
  // as offerPrice — comparing that against Premium's per-unit discount is
  // apples-to-oranges and would always show a misleading "Premium is goedkoper".
  if (isMultiBuyDeal(offer)) return null

  // Try cluster-based matching first (semantic, zero false positives)
  // Falls back to string matching for data without cluster IDs
  const match = findCatalogMatchByCluster(catalogProducts, offer)
    ?? findCatalogMatch(catalogProducts, offer.productName, offer.category)
  if (!match) return null

  const { premiumPrice } = match

  if (offer.supermarket === 'ah') {
    // AH card — compare bonus price vs Premium price
    if (isPremiumCheaper(premiumPrice, offer.offerPrice)) {
      return {
        type: 'premium-cheaper',
        premiumPrice,
        regularPrice: match.regularPrice,
        saving: formatPremiumSaving(premiumPrice, offer.offerPrice),
      }
    }
    return {
      type: 'bonus-wins',
      premiumPrice,
      regularPrice: match.regularPrice,
    }
  }

  // Non-AH card — check if AH Premium undercuts this offer
  if (isPremiumCheaper(premiumPrice, offer.offerPrice)) {
    return {
      type: 'premium-cheaper',
      premiumPrice,
      regularPrice: match.regularPrice,
      catalogProductName: match.productName,
      sourceUrl: match.sourceUrl,
      saving: formatPremiumSaving(premiumPrice, offer.offerPrice),
    }
  }

  return null
}
